import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

import Card from '@/elements/Card';
import Stack from '@/components/Stack';
import { Small, Subheading } from '@/components/Typography';
import Icon from '@/elements/Icon';
import styled from '@/styles';
import { DependabilityScore } from '@/services/workersV2API';

type Props = {
  score: DependabilityScore | null;
  onClick?: () => void;
};

const DependabilityWrapper = styled(Card, {
  padding: '16px 20px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '$neutralA4',
  },
  variants: {
    tier: {
      high: { borderTop: '3px solid #45A735' },
      medium: { borderTop: '3px solid #EB9616' },
      low: { borderTop: '3px solid #CF040C' },
      not_rated: { borderTop: '3px solid #6C757D' },
    },
  },
  defaultVariants: {
    tier: 'not_rated',
  },
});

const TierBadge = styled('span', {
  fontSize: '13px',
  fontWeight: 600,
  padding: '2px 10px',
  borderRadius: '12px',

  variants: {
    tier: {
      high: {
        color: '#45A735',
      },
      medium: {
        color: '#EB9616',
      },
      low: {
        color: '#CF040C',
      },
      not_rated: {
        color: '#6C757D',
      },
    },
  },
});

const getTierVariant = (
  tier: string | null,
): 'high' | 'medium' | 'low' | 'not_rated' => {
  if (tier === 'high' || tier === 'medium' || tier === 'low') return tier;
  return 'not_rated';
};

const DependabilityCard = ({ score, stats, onClick }: Props) => {
  const tier = getTierVariant(score?.tier ?? null);
  const label = score?.label || 'N/A';
  const lateDrops = score?.active_late_drops_count ?? 0;
  const noShows = score?.active_no_shows_count ?? 0;
  const completedShifts = score?.completed_shifts ?? 0;

  return (
    <Stack vertical gap={8}>
      <DependabilityWrapper tier={tier} onClick={onClick}>
        <Stack
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Stack vertical gap={4}>
            <Subheading weight="semibold">Dependability</Subheading>
            <Small color="lighter">
              {lateDrops} Late Drop{' | '}
              {noShows} No-show
            </Small>
          </Stack>
          <Stack gap={8} style={{ alignItems: 'center' }}>
            <TierBadge tier={tier}>{label}</TierBadge>
            <Icon
              icon={faChevronRight}
              style={{ color: '#9CA3AF', fontSize: '12px' }}
            />
          </Stack>
        </Stack>
      </DependabilityWrapper>
      <div style={{ paddingLeft: '4px' }}>
        <Small color="lighter">Total shifts worked: {completedShifts}</Small>
      </div>
    </Stack>
  );
};

export default DependabilityCard;
